import type { Translation } from '../types';

export const fr: Translation = {
  meta: { name: 'Français', english: 'French', tag: 'fr', dir: 'ltr' },

  common: {
    menu: "Menu",
    cancel: 'Annuler',
    done: 'Terminé',
    join: 'Rejoindre',
    leave: 'Quitter',
    refresh: 'Actualiser',
    save: 'Enregistrer',
    you: 'vous',
    host: 'hôte',
    ai: 'IA',
    ready: 'Prêt',
    notReady: 'Pas prêt',
  },

  conn: {
    live: 'Connecté',
    connecting: 'Connexion',
    offline: 'Hors ligne',
    connectingFull: 'Connexion…',
    reconnectBanner: 'Connexion perdue — reconnexion en cours. Votre place est gardée 3 minutes.',
  },

  nickname: {
    pickTitle: 'Choisissez un nom',
    changeTitle: 'Changer de nom',
    subtitle: 'C’est ainsi que les autres joueurs vous verront.',
    placeholder: 'ex. Joker Plus Quatre',
    label: 'Pseudo',
    letsPlay: 'C’est parti',
  },

  lobby: {
    heroTitle: 'Jouez au UNO entre amis',
    heroSubtitle: 'Sans inscription. Partagez un lien et distribuez.',
    createRoom: 'Créer un salon',
    roomCode: 'Code du salon',
    publicRooms: 'Salons publics',
    empty: 'Aucun salon public pour l’instant — créez le premier.',
    cardMeta: 'hôte {host} · tours de {seconds}s',
    stacking: 'cumul',
    badgeOpen: 'Ouvert',
    badgeFull: 'Complet',
    badgeLive: 'En partie',
    lockedTitle: 'Le salon {id} est verrouillé',
    password: 'Mot de passe',
    passwordLabel: 'Mot de passe du salon',
  },

  create: {
    title: 'Nouveau salon',
    roomName: 'Nom du salon',
    roomNamePlaceholder: 'UNO du vendredi soir',
    maxPlayers: 'Joueurs max',
    playersOption: { one: '{count} joueur', other: '{count} joueurs' },
    passwordOptional: 'Mot de passe (facultatif)',
    passwordPlaceholder: 'Laisser vide pour un salon ouvert',
    listPublic: 'Afficher dans la liste des salons publics',
    houseRules: 'Règles maison',
    submit: 'Créer',
  },

  rules: {
    turnTimer: 'Temps par tour',
    turnTimerHint: 'À l’expiration, un bot joue une carte valide au hasard à votre place.',
    startingHand: 'Main de départ',
    unoPenalty: 'Pénalité UNO',
    stackDrawTwo: 'Cumul des +2',
    stackDrawTwoHint: 'Répondre à un +2 par un autre +2 au lieu de piocher',
    stackDrawFour: 'Cumul des +4',
    stackDrawFourHint: 'Répondre à un +4 par un autre +4',
    challengeWildFour: 'Contestation du +4',
    challengeWildFourHint: 'Le joueur suivant peut contester un +4 illégal',
    strictWildFour: '+4 strict',
    strictWildFourHint: 'Jouable seulement si vous n’avez aucune carte de la couleur active',
    playDrawn: 'Jouer la carte piochée',
    playDrawnHint: 'Une carte piochée peut être jouée immédiatement',
    drawUntilPlayable: 'Piocher jusqu’à pouvoir jouer',
    drawUntilPlayableHint: 'Continuer à piocher jusqu’à obtenir une carte valide',
    hostOnly: 'seul l’hôte peut les modifier',
  },

  room: {
    copyLink: 'Copier le lien d’invitation',
    copied: '✓ Copié',
    roomLabel: 'Salon',
    locked: 'verrouillé',
    public: 'public',
    unlisted: 'non listé',
    readyStatus: '{ready} sur {total} prêts',
    botsJoining: { one: '{count} bot arrive', other: '{count} bots arrivent' },
    startsWhenReady: 'la partie démarre dès que tout le monde est prêt.',
    needMorePlayers: 'Il faut {min} joueurs pour commencer — invitez quelqu’un ou ajoutez un bot.',
    playersTitle: 'Joueurs',
    botsTitle: 'Bots IA',
    botsHostOnly: 'seul l’hôte peut le modifier',
    noBots: 'Aucun bot',
    botsWillJoin: { one: '{count} bot rejoindra', other: '{count} bots rejoindront' },
    botsHint: 'Ils prennent place au lancement de la partie. Jusqu’à {max} pour l’instant.',
    botsRoomFull: 'Le salon est complet — pas de place pour des bots.',
    soloCta: 'Vous jouez seul ? Ajoutez un bot et lancez',
    addBot: 'Ajouter un bot',
    removeBot: 'Retirer un bot',
    removePlayer: 'Retirer {name}',
    botPlaceholder: 'Bot {n}',
    defaultRoomName: 'Salon de {name}',
    imReady: 'Je suis prêt',
    cancelReady: 'Annuler « prêt »',
    offlineTag: 'hors ligne',
  },

  game: {
    yourTurn: 'À vous de jouer',
    yourTurnStack: 'À vous — cumulez ou piochez {count}',
    othersTurn: 'Au tour de {name}',
    waiting: 'En attente…',
    gameOver: 'Partie terminée',
    challengeWindow: 'Joker +4 — fenêtre de contestation',
    drawPileLeft: '{count} restantes',
    discard: 'défausse',
    draw: 'Piocher',
    takeCards: 'Prendre {count}',
    pass: 'Passer',
    uno: 'UNO !',
    judge: 'DÉNONCER',
    cardCount: { one: '{count} carte', other: '{count} cartes' },
    clockwise: 'Sens horaire',
    counterClockwise: 'Sens antihoraire',
    activeColour: 'Couleur active',
    noCards: 'Plus de cartes — bravo.',
    oneCard: 'une seule carte !',
    unoTag: 'UNO',
    forfeitedTag: 'abandon',
    offlineFor: 'hors ligne {seconds}s',
    placeTag: '{place}e',
    pickColour: 'Choisissez une couleur',
    challengeTitle: '{name} a joué un Joker +4. Contester ?',
    challengeExplain: 'S’il bluffait, il pioche {bluff}. Sinon, vous piochez {fail}.',
    challengeBtn: 'Contester',
    acceptBtn: 'Prendre {count}',
    youWin: '🎉 Vous gagnez !',
    standingsTitle: 'Partie terminée',
    points: '{points} pts',
    forfeited: 'abandon',
    backToLobby: 'Retour au salon',
    playAgain: 'Rejouer',
    waitingOthers: 'En attente des autres…',
    faceDownCard: 'Carte face cachée',
    sortByColour: "Trier par couleur",
    sortAsDealt: "Garder l’ordre de la donne",
    drawACard: 'Piocher une carte',
    drawNCards: 'Piocher {count} cartes',
  },

  events: {
    someone: 'Quelqu’un',
    unoCalledYou: 'Vous avez annoncé UNO !',
    unoCalledOther: '{name} a annoncé UNO !',
    judged: '{accuser} a pris {target} en faute — +{count} cartes',
    judgedYou: '{accuser} vous a pris en faute — +{count} cartes',
    challengeBluff: 'Bluff démasqué — {name} pioche {count}',
    challengeFailed: 'Contestation ratée — {name} pioche {count}',
    forfeited: '{name} a abandonné.',
    afkYou: 'Temps écoulé — coup joué automatiquement.',
    afkOther: '{name} a dépassé le temps — coup joué automatiquement.',
    hostChanged: '{name} est maintenant l’hôte.',
    reshuffled: 'Pioche remélangée.',
    youWin: 'Vous gagnez !',
    winner: '{name} gagne !',
    gameOver: 'Partie terminée.',
  },

  errors: {
    bad_request: 'Une erreur est survenue.',
    no_session: 'Session expirée — reconnexion en cours.',
    room_not_found: 'Ce salon n’existe pas.',
    room_full: 'Ce salon est complet.',
    wrong_password: 'Mot de passe incorrect.',
    already_in_room: 'Quittez d’abord votre salon actuel.',
    not_in_room: 'Vous n’êtes dans aucun salon.',
    not_host: 'Seul l’hôte peut faire cela.',
    game_in_progress: 'Cette partie a déjà commencé.',
    not_enough_players: 'Pas assez de joueurs.',
    stale_action: 'Quelqu’un a joué avant vous — votre coup a été annulé.',
    illegal_action: 'Ce coup n’est pas autorisé.',
    rate_limited: 'Doucement.',
    nickname_invalid: 'Ce pseudo ne convient pas.',
  },

  theme: {
    title: 'Thème des cartes',
    description: 'Les images manquantes reviennent au jeu CSS intégré.',
    builtIn: 'Intégré — aucun fichier requis',
    pngHint: 'Déposez des PNG 300×450 dans {path} et déclarez-les dans l’index des thèmes.',
  },

  language: {
    title: 'Langue',
    description: 'Appliquée immédiatement et mémorisée sur cet appareil.',
  },

  cards: {
    red: "rouge",
    yellow: "jaune",
    green: "vert",
    blue: "bleu",
    wild: "joker",
    skip: "Passe",
    reverse: "Inversion",
    draw2: "+2",
    wildCard: "Joker",
    wild4: "+4",
  },

  log: {
    title: "Journal",
    empty: "Rien pour l’instant.",
    close: "Masquer le journal",
    timestamps: "Horodatage",
    chatPlaceholder: "Dites quelque chose…",
    send: "Envoyer",
    played: "{name} joue {card}",
    drew: "{name} pioche {count}",
    skipped: "{name} passe son tour",
    reversed: "Sens inversé",
    joined: "{name} a rejoint",
    left: "{name} est parti",
    started: "Partie lancée",
  },

  profile: {
    avatar: "Avatar",
    avatarHint: "Choisissez un avatar, ou gardez votre initiale.",
  },

  leave: {
    title: "Quitter la partie ?",
    body: "La manche est en cours. Si vous partez, vous abandonnez et vos cartes retournent dans la pioche.",
    stay: "Continuer",
    confirm: "Partir quand même",
    unload: "Vous êtes en pleine partie.",
  },
};
